/** Admin review of a dated fact: preview first, explicit save, then manual filing review. */
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getLeaseHistory, previewLeaseCorrection, saveLeaseCorrection } from "../api/leaseCorrections";
import type { LeaseCommand, LeasePreview, LeaseRevision, LeaseSaved } from "../api/leaseCorrections";
import { isAmbiguous, Refused } from "../api/refusal";
import { FieldError } from "../components/FieldError";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { NotYourRole } from "../components/NotYourRole";
import { useFormChecks } from "../components/useFormChecks";
import { shortDate } from "../lib/format";
import { LEASE_LABEL } from "./organisationOptions";
import form from "./Organisation.module.css";
import styles from "./LeaseCorrections.module.css";

const CONTROLS = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';
const choices: LeaseCommand["lease_type"][] = ["owned", "finance_lease", "operating_lease", "occupied_under_licence"];
function stated(version: Pick<LeaseRevision, "lease_type" | "operates">) {
  return `${LEASE_LABEL[version.lease_type]} · ${version.operates === null ? "operation not stated" : version.operates ? "company operates it" : "company does not operate it"}`;
}
function range(from: string | null, to: string | null) {
  return `${from === null ? "From the start" : `From ${shortDate(from)}`} ${to === null ? "onwards" : `through ${shortDate(to)}`}`;
}
function recordedAt(value: string) {
  // JSON timestamps retain PostgreSQL microseconds; Date alone would collapse distinct writes.
  const fraction = value.match(/\.(\d+)/)?.[1] ?? "";
  return BigInt(Date.parse(value)) * 1000n + BigInt(fraction.padEnd(6, "0").slice(3, 6));
}
function previous(history: LeaseRevision[], version: LeaseRevision) {
  if (version.supersedes_id !== null) return history.find((row) => row.id === version.supersedes_id);
  if (version.effective_from === null) return undefined;
  const effectiveFrom = version.effective_from;
  const eventAt = recordedAt(version.created_at);
  const candidates = history.filter((row) => row.id !== version.id && recordedAt(row.created_at) <= eventAt
    && (row.superseded_at === null || recordedAt(row.superseded_at) >= eventAt)
    && (row.effective_from === null || row.effective_from < effectiveFrom))
    .sort((a, b) => (b.effective_from ?? "").localeCompare(a.effective_from ?? ""));
  const first = candidates[0];
  // Equal transaction timestamps do not establish an ordering between two corrected versions.
  return first && candidates.filter((row) => row.effective_from === first.effective_from).length === 1 ? first : undefined;
}
function effectiveEnd(history: LeaseRevision[], version: LeaseRevision) {
  const next = history.filter((row) => row.superseded_at === null && row.effective_from !== null
    && (version.effective_from === null || row.effective_from > version.effective_from))
    .sort((a, b) => (a.effective_from ?? "").localeCompare(b.effective_from ?? ""))[0]?.effective_from;
  if (!next) return null;
  const end = new Date(`${next}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

export function LeaseCorrections({ facilityId, facilityName }: { facilityId: string; facilityName: string }) {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className={form.inlineButton} data-testid={`lease-history-${facilityId}`} onClick={() => setOpen(true)}>
      Lease history and corrections
    </button>
    {open ? <LeaseDialog facilityId={facilityId} facilityName={facilityName} onClose={() => setOpen(false)} /> : null}
  </>;
}

function LeaseDialog({ facilityId, facilityName, onClose }: { facilityId: string; facilityName: string; onClose: () => void }) {
  const id = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const client = useQueryClient();
  const query = useQuery({ queryKey: ["lease-history", facilityId], queryFn: () => getLeaseHistory(facilityId), retry: false, staleTime: 0 });
  const [intent, setIntent] = useState<LeaseCommand["intent"]>("correct");
  const [targetId, setTargetId] = useState("");
  const [start, setStart] = useState("");
  const [leaseType, setLeaseType] = useState<LeaseCommand["lease_type"] | "">("");
  const [operates, setOperates] = useState<boolean | null>(null);
  const [basis, setBasis] = useState("");
  const [measuredPreview, setPreview] = useState<LeasePreview | null>(null);
  const [previewHistory, setPreviewHistory] = useState<LeaseRevision[] | null>(null);
  const [saved, setSaved] = useState<LeaseSaved | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saveError, setSaveError] = useState(false);
  const generation = useRef(0);
  const [checks, attach] = useFormChecks<HTMLDivElement>();
  const history = saved?.history ?? query.data?.history ?? [];
  // A focus-triggered history reload can move a version without a form edit. Its old preview
  // must disappear as well; the server binding remains the final concurrent-write guard.
  const preview = previewHistory === history ? measuredPreview : null;
  const active = history.filter((row) => row.superseded_at === null)
    .sort((a, b) => (a.effective_from ?? "").localeCompare(b.effective_from ?? ""));
  const latest = active.at(-1);
  const target = active.find((row) => row.id === targetId) ?? (intent === "change" ? latest : active[0]);
  const unavailable = query.error instanceof Refused && query.error.code === "lease_corrections_unavailable";
  const uncertain = saveError && isAmbiguous(error);
  /** The field's hint and, while it has one, its sentence: both are read when it takes focus. */
  const describe = (field: string, hint: string) =>
    checks.errors[field] === undefined ? hint : `${hint} ${checks.errorId(field)}`;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    title.current?.focus();
    const hold = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.current?.contains(event.target)) title.current?.focus();
    };
    document.addEventListener("focusin", hold);
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      generation.current += 1;
      document.removeEventListener("focusin", hold);
      document.body.style.overflow = oldOverflow;
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  /** Any edit drops the reviewed preview (its binding is for the old values); the field the person
   * touched also loses its own sentence, so a corrected box stops accusing them (Q-06). */
  function edit(field?: string) {
    generation.current += 1; setPreview(null); setError(null); setSaveError(false);
    if (field) checks.touch(field);
  }
  function reload() {
    edit(); checks.clear(); setSaved(null); setTargetId("");
    void query.refetch();
  }
  function keyboard(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); if (busy !== "save") onClose(); }
    if (event.key === "Tab") {
      const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>(CONTROLS) ?? []);
      if (!controls.length) { event.preventDefault(); title.current?.focus(); return; }
      const at = document.activeElement instanceof HTMLElement ? controls.indexOf(document.activeElement) : -1;
      if (at < 0 || (event.shiftKey && at === 0) || (!event.shiftKey && at === controls.length - 1)) {
        event.preventDefault(); (event.shiftKey ? controls.at(-1) : controls[0])?.focus();
      }
    }
  }
  const command: LeaseCommand | null = target && leaseType !== "" && basis.trim() !== ""
    && basis.trim().length <= 1000 && (leaseType === "owned" || operates !== null)
    && (intent === "correct" || start !== "")
    ? { intent, expected_revision_id: target.id, effective_from: intent === "correct" ? target.effective_from : start,
      lease_type: leaseType, operates: leaseType === "owned" ? true : operates, basis: basis.trim() } : null;

  /** Q-06: the button is never grey for a missing answer. Pressing it says which answer, beside its
   * own field, and sends nothing. The server stays the authority on every one of these. */
  function review() {
    if (busy) return;
    const latestStart = latest?.effective_from ?? null;
    const ok = checks.run([
      {
        field: "start",
        ok: intent === "correct" || start !== "",
        message: "Say the date the change takes effect.",
      },
      {
        field: "start",
        ok: intent === "correct" || latestStart === null || start === "" || start > latestStart,
        message: `The change must take effect after the latest recorded start, ${latestStart === null ? "" : shortDate(latestStart)}.`,
      },
      { field: "leaseType", ok: leaseType !== "", message: "Choose what the lease is." },
      {
        field: "operates",
        ok: leaseType === "owned" || leaseType === "" || operates !== null,
        message: "Say whether the company operates the site.",
      },
      {
        field: "basis",
        ok: basis.trim() !== "",
        message: "Say what was wrong or what changed, and who confirmed it.",
      },
      {
        field: "basis",
        ok: basis.trim().length <= 1000,
        message: "Keep the reason to 1000 characters or fewer.",
      },
    ]);
    if (ok) void inspect();
  }
  async function inspect() {
    if (!command || busy) return;
    const at = ++generation.current;
    setPreview(null); setError(null); setSaveError(false); setBusy("preview");
    try {
      const result = await previewLeaseCorrection(facilityId, command);
      if (at === generation.current) { setPreviewHistory(history); setPreview(result); }
    } catch (failure) { if (at === generation.current) { setError(failure); setPreview(null); } }
    finally { setBusy(null); }
  }
  async function save() {
    if (!command || !preview || preview.not_changed || busy || uncertain) return;
    setBusy("save"); setError(null); setSaveError(false);
    try {
      const result = await saveLeaseCorrection(facilityId, command, preview.preview_binding);
      setSaved(result); setPreview(null);
      client.setQueryData(["lease-history", facilityId], { facility_id: facilityId, history: result.history });
      await client.invalidateQueries({ predicate: (query) => query.queryKey[0] !== "lease-history" });
    } catch (failure) { setError(failure); setSaveError(true); setPreview(null); }
    finally { setBusy(null); }
  }

  return createPortal(<div className={styles.backdrop}>
    <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-about`}
      className={styles.dialog} onKeyDown={keyboard} data-testid="lease-correction-dialog">
      <header className={styles.header}>
        <h2 id={`${id}-title`} ref={title} tabIndex={-1}>Lease treatment · {facilityName}</h2>
        <button type="button" className={form.cancel} disabled={busy === "save"} onClick={onClose}>Close</button>
      </header>
      <div className={styles.body} ref={attach}>
        <p id={`${id}-about`}>Correct an earlier answer or record a later change. Existing issued reports remain unchanged. Review the consequences before saving.</p>
        {query.isPending ? <p role="status">Loading lease history…</p> : unavailable ?
          <p role="status" data-testid="lease-corrections-unavailable">Lease history and corrections are not available on this server yet. Your existing answer is unchanged. An Admin can return here when the dated correction service is available.</p>
          : query.error instanceof Refused && query.error.isWrongRole ? <NotYourRole action="correctLease" testId="lease-who-can" />
          : query.error ? <ReadFailed of="The lease version history" error={query.error} onRetry={reload} /> : <>
            <History history={history} />
            {saved ? <section role="status" data-testid="lease-correction-saved">
              <h3>{saved.not_changed ? "No correction was needed" : "Correction saved"}</h3>
              <Consequences effects={saved} saved />
            </section> : target ? <section aria-labelledby={`${id}-edit`}>
              <h3 id={`${id}-edit`}>Record a dated answer</h3>
              <fieldset disabled={busy === "save" || uncertain} className={styles.fields}>
                <legend className="visuallyHidden">Correction details</legend>
                <label className={`${form.field} ${styles.cell}`}><span className={form.label}>What happened?</span>
                  <select className={form.input} value={intent} onChange={(event) => {
                    edit(); const next = event.target.value === "change" ? "change" : "correct";
                    setIntent(next); setTargetId(next === "change" ? latest?.id ?? "" : "");
                  }}><option value="correct">Correct an answer that was wrong</option><option value="change">Record a later change</option></select>
                </label>
                {intent === "correct" ? <label className={`${form.field} ${styles.cell}`}><span id={`${id}-target-label`} className={form.label}>Answer to correct</span>
                  <select aria-labelledby={`${id}-target-label`} aria-describedby={`${id}-target-hint`} className={form.input} value={target.id} onChange={(event) => { edit(); setTargetId(event.target.value); }}>
                    {active.map((row) => <option key={row.id} value={row.id}>{row.effective_from === null ? "From the start" : shortDate(row.effective_from)} · {LEASE_LABEL[row.lease_type]}</option>)}
                  </select><span id={`${id}-target-hint`} className={form.hint}>The effective start stays the same. Later dated answers keep their own dates.</span>
                </label> : <div className={`${form.field} ${styles.cell}`}><label className={form.field}><span id={`${id}-start-label`} className={form.label}>Change effective from</span>
                  <input type="date" aria-labelledby={`${id}-start-label`} {...checks.field("start")} aria-describedby={describe("start", `${id}-start-hint`)} className={form.input} required value={start} min={latest?.effective_from ?? undefined}
                    onChange={(event) => { edit("start"); setStart(event.target.value); }} />
                  <span id={`${id}-start-hint`} className={form.hint}>Choose a date after the latest recorded start. Closed periods remain protected.</span>
                </label><FieldError {...checks.say("start")} /></div>}
                <p className={styles.full}>Before: <strong>{stated(target)}</strong>. {range(target.effective_from, effectiveEnd(history, target))}.</p>
                <div className={`${form.field} ${styles.cell}`}><label className={form.field}><span className={form.label}>Lease treatment</span>
                  <select className={form.input} {...checks.field("leaseType")} value={leaseType} onChange={(event) => {
                    edit("leaseType"); checks.touch("operates"); setLeaseType(choices.find((choice) => choice === event.target.value) ?? ""); setOperates(null);
                  }}><option value="">Choose…</option>{choices.map((choice) => <option key={choice} value={choice}>{LEASE_LABEL[choice]}</option>)}</select>
                </label><FieldError {...checks.say("leaseType")} /></div>
                {leaseType !== "owned" ? <div className={`${form.field} ${styles.cell}`}><label className={form.field}><span className={form.label}>Does the company operate it?</span>
                  <select className={form.input} {...checks.field("operates")} value={operates === null ? "" : operates ? "yes" : "no"} onChange={(event) => {
                    edit("operates"); setOperates(event.target.value === "" ? null : event.target.value === "yes");
                  }}><option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option></select>
                </label><FieldError {...checks.say("operates")} /></div> : <p>The company operates an owned site.</p>}
                <div className={`${form.field} ${styles.cell} ${styles.full}`}><label className={form.field}><span id={`${id}-basis-label`} className={form.label}>Reason and accountant’s basis</span>
                  <textarea aria-labelledby={`${id}-basis-label`} {...checks.field("basis")} aria-describedby={describe("basis", `${id}-basis-hint`)} className={form.input} required maxLength={1000} rows={3} value={basis} onChange={(event) => { edit("basis"); setBasis(event.target.value); }} />
                  <span id={`${id}-basis-hint`} className={form.hint}>Explain what was wrong or changed and who confirmed the treatment. This reason stays in version history, outside the immutable audit chain.</span>
                </label><FieldError {...checks.say("basis")} /></div>
              </fieldset>
              {saveError ? <WriteFailed error={error} of="this lease correction and its version history" onReload={reload} />
                : error instanceof Refused && error.isWrongRole ? <NotYourRole action="correctLease" testId="lease-who-can" />
                : error ? <p role="alert">{error instanceof Error ? error.message : "Affected filings could not be checked. Reload the history and try again."}</p> : null}
              {error instanceof Refused && error.status === 409 ? <button type="button" className={form.cancel} onClick={reload}>Reload lease history</button> : null}
              <button type="button" className={form.submit} disabled={busy !== null || uncertain} onClick={review}>
                {busy === "preview" ? "Checking affected filings…" : "Review affected filings"}
              </button>
              {preview ? <section className={styles.review} aria-labelledby={`${id}-review`} data-testid="lease-correction-preview">
                <h3 id={`${id}-review`}>Before you save</h3>
                <p>After: <strong>{command ? stated(command) : ""}</strong>. {range(preview.affected_from, preview.affected_to)}.</p>
                <Consequences effects={preview} />
                {preview.not_changed ? <p>No values or reason changed. There is nothing to save.</p> : null}
                {preview.affected_artifacts.some((artifact) => !artifact.stored_intact) ? <p role="alert">An issued report failed its integrity check. Resolve that before saving a correction.</p> : null}
                <button type="button" className={form.submit} disabled={busy !== null || preview.not_changed || preview.affected_artifacts.some((artifact) => !artifact.stored_intact)} onClick={() => void save()}>
                  {busy === "save" ? "Saving correction…" : "Save correction"}
                </button>
                <button type="button" className={form.cancel} disabled={busy === "save"} onClick={onClose}>Cancel</button>
              </section> : null}
            </section> : <p>No dated baseline is available. Nothing can be corrected until the site’s history is available.</p>}
          </>}
      </div>
    </div>
  </div>, document.body);
}

function History({ history }: { history: LeaseRevision[] }) {
  return <section aria-label="Lease version history"><h3>Version history</h3>
    {history.length === 0 ? <p>No lease versions were returned.</p> : <ol className={styles.history}>
      {[...history].sort((a, b) => recordedAt(a.created_at) === recordedAt(b.created_at) ? b.id.localeCompare(a.id) : recordedAt(a.created_at) > recordedAt(b.created_at) ? -1 : 1).map((version) => {
        const before = previous(history, version);
        return <li key={version.id} className={styles.version} data-testid={`lease-version-${version.id}`}>
          <p><strong>{stated(version)}</strong> · {range(version.effective_from, effectiveEnd(history, version))}</p>
          {before ? <p>Before: {stated(before)}. After: {stated(version)}.</p> : <p>{version.effective_from === null && version.supersedes_id === null ? "Initial undated record." : "The earlier answer cannot be established from the recorded versions and timestamps."}</p>}
          <p>{version.answered_by === null ? "Original answer attribution is not established." : <>Recorded by account <span className={styles.identifier}>{version.answered_by}</span>.</>}
            {" "}<time dateTime={version.created_at}>{version.created_at}</time></p>
          <p>Reason: {version.reason}</p>
          <p>{version.superseded_at === null ? "In force for its effective dates." : `Superseded ${version.superseded_at}.`} Version <span className={styles.identifier}>{version.id}</span>.</p>
        </li>;
      })}
    </ol>}
  </section>;
}

function Consequences({ effects, saved = false }: { effects: LeasePreview; saved?: boolean }) {
  const fresh = effects.affected_artifacts.filter((artifact) => artifact.newly_divergent).length;
  const prior = effects.affected_artifacts.filter((artifact) => artifact.already_divergent).length;
  return <>
    <p>{effects.moved_obligation_ids.length} filing{effects.moved_obligation_ids.length === 1 ? "" : "s"} {saved ? "changed" : "will change"}; {effects.invalidated_requests_count} approval request{effects.invalidated_requests_count === 1 ? "" : "s"} {saved ? "invalidated" : "will be invalidated"}.</p>
    <p>{fresh} issued report{fresh === 1 ? "" : "s"} {saved ? fresh === 1 ? "newly differs" : "newly differ" : "will newly differ"} from today’s report. {prior} already differed before this correction. Existing issued reports remain unchanged.</p>
    {effects.moved_obligation_ids.length > 0 ? <ul aria-label="Affected filings">{effects.moved_obligation_ids.map((obligation) => <li key={obligation}>Filing <span className={styles.identifier}>{obligation}</span></li>)}</ul> : null}
    {effects.affected_artifacts.length > 0 ? <ul aria-label="Issued report comparisons">{effects.affected_artifacts.map((artifact) => <li key={artifact.artifact_id}>
      Report <span className={styles.identifier}>{artifact.artifact_id}</span> · filing <span className={styles.identifier}>{artifact.obligation_id}</span>: {artifact.newly_divergent ? "newly differs" : artifact.already_divergent ? "already differed" : artifact.matches_today ? "still matches" : "differs"}; {artifact.payload_moved ? "today’s payload changes" : "today’s payload unchanged"}.{artifact.format_changed ? " Report format also changed." : ""}
    </li>)}</ul> : null}
    {effects.requires_report_review ? <ol><li>Review the affected filings in Reports, including the changed boundary and outstanding findings.</li>
      <li>Arrange any required review and approval again. Saving this correction does not approve a filing.</li>
      <li>Generate a new report explicitly when you are ready. No period is reopened and no issued report is replaced or reissued automatically.</li></ol>
      : <p>No filing boundary moved, so no affected filing needs review for this correction. Nothing is reopened or reissued automatically.</p>}
  </>;
}
